import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  DropdownMenu,
  DropdownWrapper,
  Icon,
  ItemList,
  ModalHeader,
  ModalTitle,
  ResponsiveDropdownMenu,
  ResponsiveModalMainDiv,
  ResponsiveSearchInput
} from '../styles';
import { createPortal } from 'react-dom';
import DropdownSearch from '../dropdownSearch';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { isEmpty } from '../../../helpers/common';
import { useMobileDevice } from '../../../global/useMobile';
import { ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import IconButton from '../../button/iconButton';

export interface DropdownProps {
  content: ReactNode;
  children: ReactNode;
  trigger?: 'click' | 'hover' | 'contextmenu';
  customStyle?: React.CSSProperties;
  isMinWidth?: number;
  onOutsideClick?: () => void;
  onClickItemTrigger?: () => void;
  onChangeSearch?: (value: string) => void;
  activeClassName?: string; // Optional class for active state
  setCurrentProject?: () => void;
  updateDropdownOpen?: (value: boolean) => void;
  modalTitle?: string;
  isRemoveMaxHeight?: boolean;
  isDatePickerOpen?: boolean;
  allowOutsideScroll?: boolean; // New prop to allow outside scrolling
  disablePositionChangeOnScroll?: boolean; // New prop to identify snooze dropdown
  className?: string;
  isCalendarDropdown?: boolean; // Add this
  closeOnItemClick?: boolean;
  isRemoveTopPadding?: boolean;
  position?: 'left' | 'right' | 'auto'; // Horizontal alignment preference
  searchResetKey?: number | string;
  disabled?: boolean;
  dropdownContentWidth?: string;
  searchResult?: Array<Record<string, unknown>>; // props to handle dropdown resizing and calculate position
}

const MainDropdown = forwardRef(
  (
    {
      content,
      children,
      trigger = 'click',
      customStyle = {},
      isMinWidth = 120,
      onOutsideClick,
      onChangeSearch,
      activeClassName, // Default active class
      setCurrentProject,
      updateDropdownOpen,
      modalTitle,
      isRemoveMaxHeight = false,
      isDatePickerOpen = false,
      allowOutsideScroll = false, // Default to false
      disablePositionChangeOnScroll = false, // Default to false
      className,
      isCalendarDropdown = false, // Add this
      closeOnItemClick = true,
      isRemoveTopPadding = false,
      onClickItemTrigger,
      position = 'auto', // Default to auto positioning
      searchResetKey,
      disabled = false,
      dropdownContentWidth,
      searchResult = []
    }: DropdownProps,
    ref
  ) => {
    // State to manage the dropdown's open/close status
    const [isOpen, setIsOpen] = useState(false);

    // State to variable
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const [calculateMenuPosition, setCalculateMenuPosition] = useState({ top: 0, left: 0 });
    const [openDirection, setOpenDirection] = useState<'top' | 'bottom'>('bottom');

    // References variable
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Reference for managing timeout (used for hover trigger)
    const timeoutRef = useRef<NodeJS.Timeout>();

    // Check if the screen size is mobile
    const isMobile = useMobileDevice();

    // Handle mouse enter for hover trigger
    const handleMouseEnter = useCallback(() => {
      if (trigger === 'hover' && !disabled) {
        // Clear any existing timeouts (both enter and leave)
        clearTimeout(timeoutRef.current);
        // Set a delay before opening the dropdown
        timeoutRef.current = setTimeout(() => {
          updateDropdownOpen?.(true);
          setIsOpen(true);
        }, 100); // 300ms delay before showing
      }
    }, [trigger, updateDropdownOpen, disabled]);

    // Handle close dropdown
    const handleCloseDropdown = useCallback(() => {
      setIsOpen(false);
      updateDropdownOpen?.(false);
    }, [updateDropdownOpen]);

    // Handle mouse leave for hover trigger
    const handleMouseLeave = useCallback(() => {
      if (trigger === 'hover' && !disabled) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => {
          handleCloseDropdown();
        }, 100); // 300ms delay before hiding
      }
    }, [handleCloseDropdown, trigger, disabled]);

    // Handle click for contextmenu trigger
    const handleContextMenu = useCallback(
      (event: React.MouseEvent) => {
        if (trigger === 'contextmenu' && !disabled) {
          event.preventDefault(); // Prevent default context menu

          setCurrentProject?.();
          updateDropdownOpen?.(true);

          const { clientX, clientY } = event;
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const scrollX = window.scrollX;
          const scrollY = window.scrollY;

          // Initial position before opening
          let top = clientY + scrollY;
          let left = clientX + scrollX;

          // Open dropdown at initial position
          setCalculateMenuPosition({ top, left });
          setIsOpen(true);

          requestAnimationFrame(() => {
            if (!dropdownRef.current) return;
            const dropdownRect = dropdownRef.current.getBoundingClientRect();

            let newTop = top;
            let newLeft = left;

            // Adjust if dropdown overflows viewport
            if (clientX + dropdownRect.width > viewportWidth) {
              newLeft = viewportWidth - dropdownRect.width - 10 + scrollX;
            }
            if (clientY + dropdownRect.height > viewportHeight) {
              newTop = viewportHeight - dropdownRect.height - 10 + scrollY;
            }

            // Ensure position remains unchanged if no movement is needed
            if (newLeft !== top || newTop !== left) {
              setCalculateMenuPosition((prev) => {
                if (prev.top !== newTop || prev.left !== newLeft) {
                  return { top: newTop, left: newLeft };
                }
                return prev;
              });
            }
          });
        }
      },
      // eslint-disable-next-line max-len
      [setCurrentProject, trigger, updateDropdownOpen, disabled] // Removed calculateMenuPosition to prevent stale updates
    );

    // Function to calculate the dropdown's position
    const calculatePosition = useCallback(() => {
      if (!triggerRef.current || !dropdownRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      let top = triggerRect.bottom + scrollY; // Default dropdown below trigger
      let left = triggerRect.left + scrollX;

      const offset = 2;
      const parentDropdownItem = triggerRef.current.closest('.dropdown-item');

      if (parentDropdownItem) {
        // Submenu positioning - align exactly with the hovered item
        const itemRect = triggerRef.current.getBoundingClientRect();

        // Perfect alignment with the trigger item
        top = itemRect.top + scrollY - 5;
        left = itemRect.right + scrollX + offset;

        // Ensure submenu doesn't overflow the viewport width
        if (left + dropdownRect.width > viewportWidth) {
          left = itemRect.left - dropdownRect.width + scrollX - offset;
        }

        // Ensure submenu doesn't overflow the viewport height
        if (top + dropdownRect.height > viewportHeight) {
          top = viewportHeight - dropdownRect.height + scrollY - 10;
        }

        // Ensure submenu doesn't go above the viewport
        if (top < scrollY) {
          top = scrollY + 10;
        }
      } else {
        // Apply horizontal position preference
        if (position === 'right') {
          // Align dropdown's right edge with trigger's right edge
          left = triggerRect.right - dropdownRect.width + scrollX;
        } else if (position === 'left') {
          // Align dropdown's left edge with trigger's left edge (default)
          left = triggerRect.left + scrollX;
        }
        // For 'auto', check if dropdown overflows and adjust
        if (position === 'auto' && left + dropdownRect.width > viewportWidth) {
          left = viewportWidth - dropdownRect.width - 10 + scrollX;
        }

        // Prevent dropdown from going off-screen on the left
        if (left < scrollX) {
          left = scrollX + 10;
        }

        // Check available space above and below
        const spaceBelow = viewportHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;
        // Account for the marginTop offset (4px for bottom, -4px for top)
        const requiredSpace = dropdownRect.height + 4;

        // Choose direction based on available space
        if (spaceBelow >= requiredSpace) {
          // Enough space below - open downward
          top = triggerRect.bottom + scrollY;
          setOpenDirection('bottom');
        } else if (spaceAbove >= requiredSpace) {
          // Not enough space below, but enough above - open upward
          top = triggerRect.top - dropdownRect.height + scrollY;
          setOpenDirection('top');
        } else {
          // Not enough space in either direction - use the larger space
          if (spaceBelow > spaceAbove) {
            top = triggerRect.bottom + scrollY;
            setOpenDirection('bottom');
          } else {
            top = triggerRect.top - dropdownRect.height + scrollY;
            setOpenDirection('top');
          }
        }
      }

      setDropdownPosition((prev) => {
        if (prev.top !== top || prev.left !== left) {
          return { top, left };
        }
        return prev;
      });
    }, [position]);

    useEffect(() => {
      if (isOpen) {
        calculatePosition();
      }
    }, [calculatePosition, isOpen, searchResult]);

    // Handle click for click trigger
    const handleClick = useCallback(
      (event: React.MouseEvent) => {
        if (trigger === 'click' && !disabled) {
          setIsOpen((prev) => {
            if (prev === true && !closeOnItemClick) return prev;
            if (!prev) {
              calculatePosition();
              updateDropdownOpen?.(true);
            } else {
              onClickItemTrigger?.();
              updateDropdownOpen?.(false);
            }
            return !prev;
          }); // Toggle dropdown
          event.stopPropagation();
        } else {
          handleCloseDropdown();
        }
      },
      [
        trigger,
        closeOnItemClick,
        calculatePosition,
        updateDropdownOpen,
        onClickItemTrigger,
        handleCloseDropdown,
        disabled
      ]
    );

    useEffect(() => {
      if (!isOpen) return;

      if (allowOutsideScroll) {
        // If outside scroll is allowed, do not lock the scroll
        return;
      }

      // Additional prevention for touch devices and edge cases
      const preventDefault = (e: Event) => {
        const targetElement = e.target as HTMLElement;
        // Allow scrolling if it's inside the dropdown or submenu
        if (dropdownRef.current?.contains(e.target as Node) || targetElement.closest('.dropdown-item')) {
          return;
        }
        // Prevent scrolling everywhere else
        e.preventDefault();
      };

      document.addEventListener('wheel', preventDefault, { passive: false });
      document.addEventListener('touchmove', preventDefault, { passive: false });

      return () => {
        // Restore original overflow style
        document.removeEventListener('wheel', preventDefault);
        document.removeEventListener('touchmove', preventDefault);
      };
    }, [isOpen, allowOutsideScroll]);

    // Close dropdown when clicking outside
    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        const targetElement = event.target as HTMLElement;
        if (
          targetElement.closest('.search-div') ||
          targetElement.closest('.calender') ||
          targetElement.closest('.react-calendar')
        ) {
          return;
        }
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(targetElement) &&
          triggerRef.current &&
          !triggerRef.current.contains(targetElement) &&
          !targetElement.closest('.reference-item')
        ) {
          handleCloseDropdown();
          onOutsideClick?.();
        }
      },
      [handleCloseDropdown, onOutsideClick]
    );

    // Handle blur event - fires when clicking into an iframe (cross-origin)
    const handleWindowBlur = useCallback(() => {
      if (!isOpen) return;
      setTimeout(() => {
        const active = document.activeElement;

        // Only close if focus moved to iframe
        if (active && active.tagName === 'IFRAME') {
          handleCloseDropdown();
          onOutsideClick?.();
        }
      }, 0);
    }, [isOpen, handleCloseDropdown, onOutsideClick]);

    useEffect(() => {
      if (!isOpen) return;
      document.addEventListener('mousedown', handleClickOutside, true);
      window.addEventListener('blur', handleWindowBlur, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
        window.removeEventListener('blur', handleWindowBlur, true);
      };
    }, [isOpen, handleClickOutside, handleWindowBlur]);
    // ------------------------------------------Page Scroll----------------------------------------------------------

    // Using useLayoutEffect to ensure the dropdown position is calculated before rendering
    useLayoutEffect(() => {
      if (isOpen) {
        calculatePosition();
      }
    }, [isOpen, calculatePosition]);

    useEffect(() => {
      if (!isOpen || !disablePositionChangeOnScroll) return;

      const updatePositionOnScroll = () => {
        calculatePosition();
      };

      window.addEventListener('scroll', updatePositionOnScroll, true);

      return () => {
        window.removeEventListener('scroll', updatePositionOnScroll, true);
      };
    }, [isOpen, calculatePosition, disablePositionChangeOnScroll]);

    // Functions call when we call it from parent component
    useImperativeHandle(ref, () => ({
      closeDropdown: () => {
        handleCloseDropdown();
      }
    }));

    // Render search input
    const renderSearchInput = useMemo(() => {
      return <DropdownSearch key={searchResetKey} onChangeSearch={onChangeSearch} />;
    }, [onChangeSearch, searchResetKey]);

    const renderResponsiveSearchInput = useMemo(() => {
      return (
        <ResponsiveSearchInput>
          <Icon>
            <SVGIcon
              name='search-icon'
              width={ICON_SIZE.LG}
              height={ICON_SIZE.LG}
              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill='var(--text-primary)'
            />
          </Icon>
          {renderSearchInput}
        </ResponsiveSearchInput>
      );
    }, [renderSearchInput]);

    return (
      <DropdownWrapper
        role='button'
        ref={triggerRef}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={isOpen && activeClassName ? activeClassName : className}
        trigger={trigger}
        disabled={disabled}
        dropdownContentWidth={dropdownContentWidth}>
        {content}
        {/* Dropdown menu (rendered using React Portal) */}
        {isOpen &&
          createPortal(
            isMobile ? (
              // Modal-style dropdown on mobile
              <ResponsiveModalMainDiv>
                <ResponsiveDropdownMenu ref={dropdownRef}>
                  {!isEmpty(modalTitle) && (
                    <ModalHeader>
                      <ModalTitle>{modalTitle}</ModalTitle>
                      <IconButton
                        iconName='new-close-icon'
                        size={ICON_BUTTON_SIZE.SMALL}
                        variant={ICON_BUTTON_TYPE.TERTIARY}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          e.stopPropagation();
                          handleCloseDropdown();
                        }}
                        className='icon-button'
                        type='rounded'
                        iconColorType='primary'
                      />
                    </ModalHeader>
                  )}
                  {onChangeSearch && renderResponsiveSearchInput}
                  <ItemList isRemoveMaxHeight={isRemoveMaxHeight} isDatePickerOpen={isDatePickerOpen}>
                    {children}
                  </ItemList>
                </ResponsiveDropdownMenu>
              </ResponsiveModalMainDiv>
            ) : (
              <DropdownMenu
                ref={dropdownRef}
                top={trigger === 'contextmenu' ? calculateMenuPosition.top : dropdownPosition.top}
                left={trigger === 'contextmenu' ? calculateMenuPosition.left : dropdownPosition.left}
                marginTop={openDirection === 'top' ? -4 : 4}
                isCalendarDropdown={isCalendarDropdown}
                customStyle={customStyle}
                className='dropdown-item'
                isMinWidth={isMinWidth}
                openDirection={openDirection}>
                {onChangeSearch && renderSearchInput}
                <ItemList
                  isRemoveMaxHeight={isRemoveMaxHeight}
                  isDatePickerOpen={isDatePickerOpen}
                  isRemoveTopPadding={isRemoveTopPadding}>
                  {children}
                </ItemList>
              </DropdownMenu>
            ),
            document.body
          )}
      </DropdownWrapper>
    );
  }
);

export default MainDropdown;
